import { useMutation, useQueryClient } from "react-query"
import { loginForm } from "../components/auth/LoginForm"
import { User } from "../api/user"
import { AxiosError, AxiosResponse } from "axios"
import { useDispatch, useSelector } from "react-redux"
import {
    logout,
    setAuth,
    setInitialSpeedUnit,
    setInitialUnitSystem,
    setPlatformConfigurations,
    setUser,
    updateUserSettings,
} from "../store/reducers/user.reducer"
import { persistor, RootState } from "../store"
import { routes } from "../pages/routes"

import type { ApiError } from "../shared/types/apiError.type"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useState } from "react"
import { TUser } from "../shared/types/user.type"
import { toast } from "react-toastify"
import { TrainingSetting } from "../shared/types/trainingSetting.type"
import { metrics, speedUnits } from "../enums/register.enums"
import { getCorrectDateFormat } from "../shared/utils/DateUtils"
import { TThirdPartyApp } from "../shared/types/thirdParty.type"
import { hydraTransform } from "../shared/utils/hydraTransformer"
import { TKeyParamLayout } from "../shared/types/exam.type"
import { jwtDecode } from "jwt-decode"
import { useLocales } from "./useLocales"

export const useAuth = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { currentLang} = useLocales()
    const isAuthenticated = useSelector<RootState>(state => state.user.isAuthenticated)
    const auth = useSelector<RootState>(state => state.user.user) as TUser
    const speedUnit = useSelector<RootState>(state => state.user.user?.default_speed_unit) as string
    const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined



    const renderPolo = useMemo(() => {
        return new URLSearchParams(window.location.search).get('user_type') === 'POLO'
    }, [])


    const token = localStorage.getItem('token')

    const [layoutKeys, setLayoutKeys] = useState<TKeyParamLayout[]>([])

    const queryClient = useQueryClient()
    //login Mutation
    const loginMutation = useMutation((data: loginForm) => User.login(data))

    //get platform configurations
    const platformConfigurationsMutation = useMutation((userId: string) => User.getConfigurations(userId))

    //register Mutation
    const registerMutation = useMutation((data: loginForm) => User.register(data))

    //update Mutation
    const updateMutation = useMutation((data: Partial<TUser>) => User.update(data))

    //get Auth User
    const getUserMutation = useMutation(() => User.getUser())

    //get Auth User
    const getUsersMutation = useMutation((params?: string[]) => User.getUsers(params))

    //reset new password Mutation
    const resetPasswordMutation = useMutation((data: { password: string, token: string }) => User.resetPassword(data))

    //request new password Mutation
    const requestMutation = useMutation((data: { email: string }) => User.requestResetPassword(data))

    //update Settings mutation
    const updateSettingsMutation = useMutation(({ id, data }: {
        id: number,
        data: Partial<TrainingSetting>
    }) => User.updateSettings(id, data))


    //Patch third Party 
    const updateThirdPartyMutation = useMutation(({ id, data }: {
        id: number,
        data: Partial<TThirdPartyApp>
    }) => User.updateThirdParty(id, data))


    //Search user by email
    const searchUserByEmail = useMutation((email: string) => User.searchByEmail(email))


    //Get user params layout

    const getKeyParamsLayout = useMutation(() => User.getParamsLayout(auth?.["@id"],currentLang), {
        onSuccess: (response) => {
            let data = hydraTransform(response.data)
            setLayoutKeys(data)
        }
    })
    //login
    const login = async (data: loginForm) => {

        let rememberMe = data.rememberMe

        delete data?.rememberMe


        try {
            let result = await loginMutation.mutateAsync(data)

            if (rememberMe && result.data?.refresh_token) {
                setRefreshToken(result.data.refresh_token)
            }

            setToken(result.data.token)


            return result.data
        } catch (error: any) {
            return error

        }


    }
    //register
    const register = async (data: loginForm) => {

        try {
            let result = await registerMutation.mutateAsync(data)
            return result.data
        } catch (error) {
            console.log({ error });
        }
    }

    //Fetch platform Confi
    const getPlatformConfig = async (user: string) => {
        try {
            let config = await platformConfigurationsMutation.mutateAsync(user)
            dispatch(setPlatformConfigurations(config?.data ?? []))
        } catch (error) {
            console.log('Cannot fetch platform config');

        }
    }

    const updateUser = async (data: Partial<TUser> & { apply_kp_defaults?: boolean | never, reset_default_settings?: boolean | never }) => {
        return updateMutation.mutateAsync(data)
            .then((result) => {
                dispatch(setUser(result.data))
                dispatch(setInitialSpeedUnit(result.data.default_speed_unit))
                dispatch(setInitialUnitSystem(result.data.unit_system))
                toast(t('defaultSuccessMessage'), { type: 'success' })
                return result
            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })
                return err
            })
    }

    //resetPassword
    const resetPassword = async (data: { password: string, token: string }) => {
        try {
            await resetPasswordMutation.mutateAsync(data)
            toast(t('change_password:flash:success'), { type: 'success' })

        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }
    }

    //send reset email
    const requestResetPassword = async (email: string) => {

        try {
            await requestMutation.mutateAsync({ email })
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }

    }

    const getUser = () => {
        getUserMutation.mutateAsync()
            .then((result) => {
                dispatch(setUser(result.data))

            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })

            })
    }

    const disconnect = () => {
        dispatch(logout());  // ✅ Clears all Redux state
        queryClient.removeQueries()
        localStorage.clear()
        persistor.purge()
    }



    const updateUserTraingSetting = async (id: number, settings: Partial<TrainingSetting>, showSuccessMessage = true) => {
        try {
            let { data } = await updateSettingsMutation.mutateAsync({ id, data: settings })
            dispatch(updateUserSettings(data))


            if (showSuccessMessage) {
                toast(t('rights:success'), { type: 'success' })
            }

        } catch (err) {
            toast(t('error:generic'), { type: 'error' })

        }
    }


    const updateThirdParty = async (id: number, data: Partial<TThirdPartyApp>) => {
        try {
            await updateThirdPartyMutation.mutateAsync({ id, data })
            queryClient.refetchQueries({ queryKey: ['auth'] })

            //Show message based on active value
            toast(t(data.active ? 'thirdPartyApi:messageAccessRequestAccepted' : 'thirdPartyApi:messageAccessRequestRevoked'), { type: 'success' })

        } catch (error) {
            toast(t('error:generic'), { type: 'error' })

        }
    }


    //User avatar image
    const user_avatar_uri = useMemo(() => {
        return auth?.avatar_url ?? "https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/no-profile-picture-icon.png"
    }, [auth?.avatar_url])


    //get speed Unit

    const getSpeedUnit = (sp?: string, m?: string) => {

        let spUn = sp ?? speedUnit
        let mtrc = m ?? metric

        if (spUn === 'm/min') {
            return speedUnits.find(e => e.value === spUn)
        } else {
            let find = speedUnits.find(e => e.value === spUn && e.enabled === mtrc)

            if (!find) return
            if (mtrc === 'metric') {
                if (spUn === 'km/h') {
                    return { ...find, value: 'km/h' }
                }
                if (spUn === 'Red. km') {
                    return { ...find, value: 'Red. km' }
                }
            } else {
                if (spUn === 'km/h') {
                    return { ...find, value: 'mph' }
                }
                if (spUn === 'Red. km') {
                    return { ...find, value: 'Mil. red.' }
                }
            }

        }

    }

    const setToken = (token: string) => {
        localStorage.setItem('token', token)
        dispatch(setAuth(true))
    }

    const setShareToken = (token: string) => {
        localStorage.setItem('share_token', token)
        dispatch(setAuth(true))
    }

    const setRefreshToken = (token: string) => {
        localStorage.setItem('refresh_token', token)
    }


    const getMetric = () => {
        return metric === 'metric' ? t('compare:meters') : t('scale:pieds')
    }


    const isTokenHasIu = useMemo(() => {

        if (!token) return false
        //decode token
        try {
            let decoded_token = jwtDecode(token)
            return 'iu' in decoded_token
        } catch (error) {
            return false
        }


    }, [token])

    const isAdmin = useMemo(() => {
        return isTokenHasIu || auth?.roles?.includes('ROLE_SUPER_ADMIN')
    }, [auth, isTokenHasIu])

    const getUserDateFormat = useMemo(() => {
        if (auth?.default_date_format) {
            return getCorrectDateFormat(auth.default_date_format) + ' HH:mm'
        } else {
            return 'DD/MM/YYYY HH:mm'
        }
    }, [auth?.id])


    const getUserMetric = useMemo(() => {
        return t(metrics.find(e => e.value === auth?.unit_system)?.key ?? 'metric')
    }, [auth])

    const getUserLang = useMemo(() => {
        return auth?.lang ?? 'en'
    }, [auth])

    const getUserSpeedUnit = useMemo(() => {

        return auth?.default_speed_unit ?? 'km/h'
    }, [auth])

    const getUserAmplitudeUnit = useMemo(() => {

        return auth?.unit_system === 'metric' ? t('graph:metres') : t('graph:pieds')
    }, [auth])


    const isUserInAuthorizedDomains = (domains: string[]) => {
        let domainEmail = auth.email.split('@')[1] //retrieve only the domain

        return domains.includes(domainEmail)
    }


    const isUserTypePolo = renderPolo || auth?.type === 'POLO'

    const isUserHasAdaptiveDisplay = auth?.adaptative_display || false

    return {
        login,
        register,
        resetPassword,
        requestResetPassword,
        getUser,
        updateUser,
        isUserFetching: getUserMutation.isLoading,
        isUserError: getUserMutation.isError,
        isLoading: loginMutation.isLoading || registerMutation.isLoading || requestMutation.isLoading || resetPasswordMutation.isLoading || updateMutation.isLoading,
        isError: loginMutation.isError || registerMutation.isError || requestMutation.isError || resetPasswordMutation.isError || updateMutation.isError,
        error: (loginMutation.error || registerMutation.error || requestMutation.error || resetPasswordMutation.error || updateMutation.error) as AxiosError,
        isAuthenticated,
        auth,
        isUpdateUserSuccess: updateMutation.isSuccess,
        user_avatar_uri,
        updateUserTraingSetting,
        settingsUpdating: updateSettingsMutation.isLoading,
        getSpeedUnit,
        getMetric,
        setToken,
        getUserMutation,
        isAdmin,
        disconnect,
        getUserDateFormat,
        getUserMetric,
        getUserLang,
        getUserSpeedUnit,
        getUserAmplitudeUnit,
        updateThirdParty,
        thirdPartyUpdating: updateThirdPartyMutation.isLoading,
        getKeyParamsLayout,
        layoutKeys,
        getUsersMutation,
        setShareToken,
        isUserInAuthorizedDomains,
        getPlatformConfig,
        searchUserByEmail,
        isUserTypePolo,
        metric,
        isUserHasAdaptiveDisplay
    }
}